/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { MdOutlineVisibility as RevealIcon, MdOutlineVisibilityOff as HideIcon } from 'react-icons/md';
import { ScrollToFormikError } from '../elements/ScrollToFormikError';
import { FormikErrors } from 'formik';
import moment from 'moment';
import { motion } from 'framer-motion';
// import "intl-tel-input/build/css/intlTelInput.css";
// import intlTelInput from "intl-tel-input";

interface IInputProps {
  id?: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  prepend?: JSX.Element | React.ReactElement | string | number | any;
  append?: JSX.Element | React.ReactElement | string | number | any;
  type?:
  | 'text'
  | 'number'
  | 'email'
  | 'tel'
  | 'password'
  | 'search'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'file'
  | 'url'
  | 'hidden';
  name?: string;
  // countryCode?: boolean;
  // defaultCountryCode?: "gh"
  placeholder?: string;
  min?: number | string;
  max?: number | string;
  minDate?: string; // ISO date string format: 'YYYY-MM-DD'
  maxDate?: string; // ISO date string format: 'YYYY-MM-DD'
  step?: number; // 1 (day), 7 (week), etc.
  disabledDays?: number[]; // [0, 1, 2, 3, 4, 5, 6] where 0 is Sunday
  isClearable?: boolean; // Show clear button
  closeOnSelect?: boolean; // Close date picker after selection
  dateFormat?: string; // Custom date format (e.g., 'dd/MM/yyyy')
  highlightDates?: string[]; // Array of dates to highlight in format 'YYYY-MM-DD'
  value?: string | number | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void | null | any;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<any, Element>) => void | null | any;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  error?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  required?: boolean;
  helperText?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  autoComplete?: boolean;
  className?: string;
  wrapperClasses?: string;
  labelClasses?: string;
  showPasswordIcon?: boolean;
  wiggle?: boolean;
  readOnly?: boolean;
  prependClasses?: string;
  scrollToFormik?: boolean;
  showRing?: boolean;
  appendLabelIcon?: JSX.Element;
  autoFocus?: boolean;
}

function Input({
  id,
  label,
  description,
  disabled = false,
  prepend,
  append,
  type = 'text',
  name,
  placeholder,
  readOnly = false,
  min,
  max,
  minDate,
  maxDate,
  value,
  onChange,
  onKeyDown,
  onBlur,
  onFocus,
  onClick,
  error,
  required = false,
  helperText,
  autoComplete = false,
  className,
  wrapperClasses,
  labelClasses,
  showPasswordIcon = false,
  // countryCode = false,
  wiggle = true,
  prependClasses,
  scrollToFormik = true,
  step,
  disabledDays = [],
  isClearable = false,
  closeOnSelect = true,
  dateFormat = 'YYYY-MM-DD',
  highlightDates = [],
  showRing,
  appendLabelIcon,
  autoFocus = false
}: IInputProps): JSX.Element {
  // const inputRef = createRef<HTMLInputElement>();
  const [showPassword, setShowPassword] = useState(false);
  const [currentType, setCurrentType] = useState(type);
  const fieldRef = useRef<HTMLInputElement>(null);

  const toggleRevealHidePassword = () => {
    setCurrentType(currentType == 'password' ? 'text' : 'password');
    setShowPassword(!showPassword);
  };

  React.useEffect(() => {
    autoFocus && fieldRef?.current?.focus();
  }, [])


  return (
    <div className={clsx('w-full', { ['animate-wiggle']: error && required }, wrapperClasses)}>
      {/* Label */}
      {label && (
        <div className='flex items-center'>
          <label
            htmlFor={id}
            className={clsx('block text-sm font-medium text-gray-700 dark:text-darkBg-mainText', {
              ['text-danger-main']: error && required
            },
              labelClasses)}>
            {label} <span className="text-danger-main">{required && '*'}</span>

          </label>
          {appendLabelIcon && appendLabelIcon}
        </div>
      )}
      {description && (
        <p
          className={clsx('text-xs text-gray-400 dark:text-darkBg-subText italic', {
            ['text-danger-border']: error && required
          })}>
          {description}
        </p>
      )}

      <div className={clsx('relative', { ['mt-1']: Boolean(label) })}>
        <div className="relative w-full">
          <input
            ref={fieldRef}
            type={currentType}
            onClick={onClick}
            name={name}
            id={id}
            readOnly={readOnly}
            onChange={onChange}
            onKeyDown={(e) => {
              // Call the original onKeyDown if it exists
              onKeyDown?.(e);

              // For date inputs, prevent manual date input
              if (type === 'date' && e.key !== 'Tab' && e.key !== 'Enter' && e.key !== 'Escape') {
                e.preventDefault();
              }
            }}
            onBlur={onBlur}
            onFocus={onFocus}
            className={clsx(
              'relative border !h-[40px] dark:[&::-webkit-calendar-picker-indicator]:invert border-gray-300 dark:border-darkBg-border shadow-sm text-left cursor-default rounded-full',
              `focus:outline-none focus:ring-1 ${showRing ? 'focus:ring-primary-surface' : ' focus:ring-transparent'}  focus:border-primary-main sm:text-sm`,
              'flex items-center placeholder:text-gray-400 dark:placeholder:text-darkBg-subText',
              type === 'checkbox' ? 'h-4 w-4 rounded-sm text-primary-main' : 'w-full py-2',
              type !== 'checkbox' ? (prepend ? 'pl-12' : 'pl-3') : null,
              type !== 'checkbox' ? (append ? 'pr-10' : 'pr-3') : null,
              type === 'date' && isClearable && value ? 'pr-10' : '',
              disabled ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed dark:text-darkBg-mainText' : 'bg-white dark:bg-darkBg dark:text-white',
              {
                ['bg-red-200 !border-danger-main focus:ring-danger-main focus:border-danger-border']:
                  error && required
              },
              error && '!border-danger-main',
              className,
              { ['animate-wiggle']: Boolean(error) && wiggle },
              readOnly && 'border-none',
              { ['border-red-200 focus:ring-red-200 focus:border-red-200']: !value && required }
            )}
            placeholder={placeholder}
            disabled={disabled}
            {...(type === 'date' ? {
              min: minDate || min,
              max: maxDate || max,
              step: step,
              'data-close-on-select': closeOnSelect,
              'data-highlight-dates': JSON.stringify(highlightDates),
              'data-date-format': dateFormat,
              'data-disabled-days': JSON.stringify(disabledDays)
            } : {})}
            value={
              value === null ? '' :
                type === 'date' && value
                  ? typeof value === 'string' && value.includes('T')
                    ? value.split('T')[0]
                    : moment(value).isValid()
                      ? moment(value).format('YYYY-MM-DD')
                      : ''
                  : type === 'email'
                    ? String(value || '').trim().toLowerCase()
                    : type === 'number'
                      ? value ?? ''
                      : value
            }

            autoComplete={autoComplete ? undefined : String(autoComplete)}
          />

          {prepend && (
            <div
              className={clsx(
                'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none w-12 sm:w-15 h-full text-[#004E60]',
                prependClasses
              )}>
              {prepend}
            </div>
          )}

          {type === 'date' && isClearable && value && (
            <div
              className="absolute inset-y-0 right-0 p-3 flex items-center cursor-pointer text-gray-400 dark:text-darkBg-subText hover:text-gray-600 dark:hover:text-darkBg-mainText"
              onClick={() => {
                if (onChange) {
                  const event = { target: { name: name || '', value: '' } };
                  onChange(event as React.ChangeEvent<HTMLInputElement>);
                }
              }}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}

          {type === 'password' && showPasswordIcon && (
            <div className="absolute inset-y-0 right-0 p-3 flex items-center cursor-pointer text-[#004E60] dark:text-primary-main">
              {showPassword ? (
                <HideIcon className="h-5 w-5" onClick={toggleRevealHidePassword} />
              ) : (
                <RevealIcon className="h-5 w-5" onClick={toggleRevealHidePassword} />
              )}
            </div>
          )}

          {append && (
            <div className={`absolute inset-y-0 ${type === 'number' ? 'right-3' : 'right-0'} rounded-r  flex items-center text-[#004E60]`}>
              {append}
            </div>
          )}

        </div>
      </div>

      {/* Helper Text */}
      <div className={clsx('text-xs text-gray-400 dark:text-darkBg-subText', { ['!text-danger-main']: error })}>
        {helperText && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 text-sm text-red-600 flex items-center font-medium"
          >
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {helperText && typeof helperText === 'string' ? helperText : undefined}
          </motion.p>
        )}
      </div>

      {scrollToFormik && <ScrollToFormikError fieldName={name ?? id ?? ''} fieldRef={fieldRef} />}
    </div>
  );
}

export default Input;
